/* eslint-disable no-mixed-spaces-and-tabs */
import Vue from "vue";
import NProgress from "nprogress";
import storeAxios from "@/store/axios";
import { deepMerge, isNil, filterNil } from "@/utils";
import { setOrReplace } from "../../helpers";
import toFrontend from "./dataMappers/simulationToFrontend";
import toBackend from "./dataMappers/simulationToBackend";
import simulationOptions from "./models/simulationOptions";

export default {
	namespaced: true,

	state() {
		return {
			simulationOptions: simulationOptions,
			simulations: [],
			simulationSummaries: [],
		};
	},

	mutations: {
		setSimulationSummaries(state, { val }) {
			state.simulationSummaries = [...val];
		},

		setSimulationSummariesItem(state, { val }) {
			setOrReplace(state.simulationSummaries, val);
		},

		setSimulation(state, { val }) {
			setOrReplace(state.simulations, val);
		},

		removeSimulation(state, { id }) {
			state.simulations = state.simulations.filter((item) => item.id !== id);
		},

		removeSimulationSummary(state, { id }) {
			state.simulationSummaries = state.simulationSummaries.filter(
				(item) => item.id !== id
			);
		},
	},

	actions: {
		fetchSimulationSummaries({ commit }) {
			return storeAxios
				.get("Calculations")
				.then(({ data }) => {
					const summaries = data.map((item) => {
						return {
							...item,
							name: item.name ?? "Untitled",
						};
					});
					commit("setSimulationSummaries", { val: summaries });
					return summaries;
				})
				.catch((err) => {
					console.error(err);
					throw err;
				});
		},

		fetchSimulation({ commit }, id) {
			return storeAxios
				.get(`Calculations/${id}`)
				.then(({ data }) => {
					const simulation = toFrontend(data);

					commit("setSimulation", {
						val: simulation,
					});

					return simulation;
				})
				.catch((err) => {
					console.log("fetchSimulation", err);
					throw err;
				});
		},

		async duplicateSimulation({ dispatch, getters }, { id }) {
			NProgress.start();

			return dispatch("fetchSimulation", id)
				.then((simulation) => {
					const { id: extractId, calculationStatus, ...rest } = simulation;

					return dispatch("createSimulation", {
						simulation: {
							...rest,
							creationDate: new Date().toISOString(),
							name: `${rest.name} (copy)`,
						},
					});
				})
				.then((simulation) => {
					Vue.notify({
						type: "success",
						title: "Simulation duplicated successfully.",
					});

					return simulation;
				})
				.catch((err) => {
					Vue.notify({
						type: "error",
						title: "Failed to duplicate simulation. Please try again.",
					});
					console.log("duplicateSimulation", err);
					throw err;
				})
				.finally(() => {
					NProgress.done();
				});
		},

		createSimulation({ commit, dispatch }, { simulation }) {
			const backendData = toBackend(simulation);

			return storeAxios.post("Calculations", backendData).then(({ data }) => {
				const simulation = toFrontend(data);
				commit("setSimulation", { val: simulation });

				// keep summaries in sync
				dispatch("fetchSimulationSummaries");

				return simulation;
			});
		},

		updateSimulation(
			{ commit, dispatch },
			{ id, simulation: updatedSimulation }
		) {
			const backendData = toBackend(updatedSimulation);

			return storeAxios
				.patch(`Calculations/${id}`, backendData)
				.then(({ data }) => {
					const simulation = toFrontend(data);

					commit("setSimulation", { val: simulation });

					// keep summaries in sync
					dispatch("fetchSimulationSummaries");

					return simulation;
				});
		},

		updateSimulationMetadata(
			{ commit },
			{ id, name, description, chosenResultId, steelGrades, project }
		) {
			const payload = filterNil({
				name,
				description,
				chosenResultId,
				//steelGrades,
				project,
			});

			return storeAxios
				.patch(`Calculations/${id}`, payload)
				.then(({ data }) => {
					const simulation = toFrontend(data);
					commit("setSimulation", { val: simulation });
					return simulation;
				});
		},

		updateSolutionSelectionDetails(
			{ commit },
			{ id, chosenResultId, steelGrades }
		) {
			return storeAxios
				.patch(`Calculations/${id}`, {
					chosenResultId,
					//steelGrades,
				})
				.then(({ data }) => {
					const simulation = toFrontend(data);
					commit("setSimulation", { val: simulation });
				});
		},

		async initCalculation({ dispatch }, { id }) {
			const subscriptions = [];

			storeAxios.post(`Calculations/${id}/calculate`).then((result) => {
				const poll = () => {
					dispatch("fetchSimulation", id).then((simulation) => {
						subscriptions.forEach((cb) => cb?.(simulation));

						if (simulation.calculationStatus === "IN_PROGRESS") {
							setTimeout(poll, 5000);
						} else if (simulation.calculationStatus == "ERROR") {
							Vue.notify({
								type: "error",
								title: "Erro ao calcular. Tente novamente mais tarde.",
							});
						} else if (simulation.calculationStatus == "COMPLETE") {
							Vue.notify({
								type: "success",
								title: "Cálculo terminou com sucesso.",
							});
						}
					});
				};

				poll();

				return result;
			});

			return {
				subscribe(cb) {
					const subscriptionIdx = subscriptions.length;
					subscriptions.push(cb);

					return {
						cancel() {
							subscriptions[subscriptionIdx] = null;
						},
					};
				},
			};
		},

		downloadCalculationNote({ dispatch }, { id }) {
			const simulation = this.getters["simulations/getSimulation"](id);

			if (!simulation) {
				return dispatch("fetchSimulation", id).then(() => {
					return dispatch("downloadCalculationNote", { id });
				});
			}

			if (isNil(simulation.chosenSolution)) {
				throw new Error({
					response: {
						data: {
							message: "No solution chosen",
						},
					},
				});
			}

			return storeAxios
				.get(`Calculations/${id}/calculation-note`)
				.then((res) => {
					const a = document.createElement("a");
					a.href = `data:application/pdf;base64,${res.data.base64Pdf}`;
					a.download = `Simulação ${simulation.name} - Note de cálculo.pdf`;
					a.click();
				});
		},

		deleteSimulation({ commit, dispatch }, { id }) {
			NProgress.start();
			return storeAxios
				.delete(`Calculations/${id}`)
				.then(() => {
					Vue.notify({
						type: "success",
						title: "Simulation deleted successfully.",
					});
					commit("removeSimulationSummary", { id });
					commit("removeSimulation", { id });

					// keep summaries in sync
					dispatch("fetchSimulationSummaries");
				})
				.catch((err) => {
					Vue.notify({
						type: "error",
						title: "Failed to delete simulation. Please try again.",
					});
					console.error(err);
					throw err;
				})
				.finally(() => {
					NProgress.done();
				});
		},
	},

	getters: {
		getSimulation(state) {
			return (id) => {
				return state.simulations.find((sim) => sim.id === id);
			};
		},

		getSimulationSummary(state) {
			return (id) => {
				return state.simulationSummaries.find((sim) => sim.id === id);
			};
		},

		getOption(state) {
			return (simulationOptionsKey, optionId) => {
				if (!state.simulationOptions[simulationOptionsKey].options) {
					return null;
				}
				return state.simulationOptions[simulationOptionsKey].options.find(
					({ id }) => id === optionId
				);
			};
		},
	},
};
